import React, { useState, useEffect, useRef } from 'react'
import { format, eachDayOfInterval, addDays } from 'date-fns'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { ColumnGroup } from 'primereact/columngroup'
import { Row } from 'primereact/row'
import { Card } from 'primereact/card'
import { ProgressBar } from 'primereact/progressbar'
import { Message } from 'primereact/message'
import { MkSearchCalAvailResponse } from '@features/search/services/searchTypes'
import { fetchAllResortAvailPage } from '@features/search/services/MkApiSearch'
import { useResorts } from '@resort/context/ResortContext'
import { useAuthHeaders } from '@hooks/useAuthHeaders'

const AVAIL_TABLE_DAYS = 90

const shortenResortName = (name: string): string => {
  if (!name) return ''
  return name
    .replace(/Club Wyndham/g, 'CW')
    .replace(/Worldmark/g, 'WM')
    .replace(/Margaritaville Vacation Club by Wyndham -/g, 'Margaritaville')
    .replace(/Sports Illustrated Resorts/g, 'SIR')
}

const availLevelColors = {
  none: '#f8f9fa',
  low: '#f8d7da',
  medium: '#fff3cd',
  high: '#d4edda',
}

const monthColors = ['#e6f7ff', '#fff0f6']

interface ResortAvailabilityDataRow {
  resortKey: string
  resortName: string
  [key: string]: string | number | { availCount: number; availLevel: string } | undefined
}

export const MkAvailTable: React.FC = () => {
  const today = new Date()
  const startDate = today
  const endDate = addDays(today, AVAIL_TABLE_DAYS)

  const [loading, setLoading] = useState(false)
  const [loadedCount, setLoadedCount] = useState(0)
  const [tableData, setTableData] = useState<ResortAvailabilityDataRow[]>([])
  const [dateColumns, setDateColumns] = useState<any[]>([])
  const [monthGroups, setMonthGroups] = useState<any[]>([])
  const [error, setError] = useState<string | undefined>()

  const { resorts } = useResorts()
  const authHeaders = useAuthHeaders()
  const hasFetched = useRef(false)

  const renderAvailabilityCell = (rowData: ResortAvailabilityDataRow, dateKey: string, isFirstDayOfMonth = false) => {
    const cellData = rowData[dateKey] as { availCount: number; availLevel: string } | undefined
    const availLevel = cellData?.availLevel || 'none'
    const availCount = cellData?.availCount
    const backgroundColor = availLevelColors[availLevel as keyof typeof availLevelColors]
    return (
      <div
        style={{
          backgroundColor,
          width: '100%',
          height: '100%',
          padding: '0.5rem',
          textAlign: 'center',
          borderLeft: isFirstDayOfMonth ? '2px solid #999' : undefined,
        }}>
        {availCount != null ? availCount : '-'}
      </div>
    )
  }

  const buildColumns = () => {
    const dateRange = eachDayOfInterval({ start: startDate, end: endDate })

    const columns = dateRange.map((date, index) => {
      const dateStr = format(date, 'yyyy-MM-dd')
      const isFirstDayOfMonth = date.getDate() === 1 || index === 0
      return {
        field: dateStr,
        header: format(date, 'd'),
        isFirstDayOfMonth,
        body: (rowData: ResortAvailabilityDataRow) => renderAvailabilityCell(rowData, dateStr, isFirstDayOfMonth),
      }
    })

    const months: { [key: string]: string[] } = {}
    dateRange.forEach((date) => {
      const monthKey = format(date, 'yyyy-MM')
      if (!months[monthKey]) months[monthKey] = []
      months[monthKey].push(format(date, 'yyyy-MM-dd'))
    })

    const groups = Object.entries(months)
      .filter(([, dates]) => dates.length >= 3)
      .map(([key, dates]) => {
        const [yearStr, monthStr] = key.split('-')
        const monthDate = new Date(parseInt(yearStr, 10), parseInt(monthStr, 10) - 1, 1)
        return { monthName: format(monthDate, 'MMM yyyy'), dates }
      })

    setDateColumns(columns)
    setMonthGroups(groups)
    return dateRange
  }

  const loadData = async () => {
    if (!resorts || resorts.length === 0) return
    setLoading(true)
    setLoadedCount(0)
    setError(undefined)

    const dateRange = buildColumns()
    const allAvail: Record<string, MkSearchCalAvailResponse> = {}

    try {
      let page = 1
      while (true) {
        const pageData = await fetchAllResortAvailPage(page, authHeaders)
        if (!pageData || Object.keys(pageData).length === 0) break
        Object.assign(allAvail, pageData)
        setLoadedCount(Object.keys(allAvail).length)

        const rows: ResortAvailabilityDataRow[] = resorts.map((resort) => {
          const avail = allAvail[resort.resourceKey]
          const row: ResortAvailabilityDataRow = {
            resortKey: resort.resourceKey,
            resortName: shortenResortName(resort.prodName || resort.name),
          }
          dateRange.forEach((date) => {
            const dateStr = format(date, 'yyyy-MM-dd')
            const dayData = avail?.dayAvail?.[dateStr]
            if (dayData) {
              row[dateStr] = { availCount: dayData.availCount, availLevel: dayData.availLevel }
            }
          })
          return row
        })
        setTableData(rows)
        page++
      }
    } catch (e: any) {
      setError(e?.message ?? 'Error loading availability data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!resorts || resorts.length === 0) return
    if (hasFetched.current) return
    hasFetched.current = true
    loadData()
  }, [resorts])

  const renderColumnGroup = () => (
    <ColumnGroup>
      <Row>
        <Column
          header="Resort"
          style={{ width: '400px', minWidth: '400px', maxWidth: '400px', position: 'sticky', left: 0, top: 0, zIndex: 2, background: 'white' }}
          rowSpan={2}
        />
        {monthGroups.map((month, index) => (
          <Column
            key={month.monthName}
            header={month.monthName}
            colSpan={month.dates.length}
            style={{ textAlign: 'left', borderBottom: '1px solid #dee2e6', position: 'sticky', top: 0, zIndex: 1, background: monthColors[index % 2], height: '38px' }}
          />
        ))}
      </Row>
      <Row>
        {dateColumns.map((col) => {
          const monthIndex = monthGroups.findIndex((month) => month.dates.includes(col.field))
          return (
            <Column
              key={col.field}
              header={col.header}
              style={{ textAlign: 'center', padding: '0.5rem', position: 'sticky', top: '38px', zIndex: 1, background: monthIndex >= 0 ? monthColors[monthIndex % 2] : 'white' }}
            />
          )
        })}
      </Row>
    </ColumnGroup>
  )

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Resort Availability Table</h1>

      <Card className="mb-4">
        <div className="grid">
          <div className="col-12">
            <h3>
              Date Range: {format(startDate, 'MMMM d, yyyy')} - {format(endDate, 'MMMM d, yyyy')}
            </h3>
            <p>Showing availability for &quot;All Suites&quot; across all resorts</p>

            {loading && (
              <div className="mt-3">
                <ProgressBar mode="indeterminate" style={{ height: '20px' }} />
                <div className="mt-2">Loading availability for all resorts{loadedCount > 0 ? ` (${loadedCount} of ${resorts.length})` : '...'}</div>
              </div>
            )}

            {error && (
              <Message severity="error" text={error} className="mt-3 w-full" />
            )}
          </div>
        </div>
      </Card>

      <div className="card">
        <DataTable
          value={tableData}
          loading={loading}
          scrollable
          scrollHeight="600px"
          className="mk-datatable"
          emptyMessage="No availability data found"
          headerColumnGroup={renderColumnGroup()}
          showGridlines>
          <Column
            field="resortName"
            header="Resort"
            style={{ width: '400px', minWidth: '400px', maxWidth: '400px', position: 'sticky', left: 0, background: 'white', zIndex: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}
            frozen
          />
          {dateColumns.map((col) => (
            <Column
              key={col.field}
              field={col.field}
              header={col.header}
              body={col.body}
              style={{ width: '60px', textAlign: 'center' }}
            />
          ))}
        </DataTable>
      </div>

      <div className="mt-4 p-4 border rounded">
        <h3>Availability Legend</h3>
        <div className="grid">
          <div className="col-6 md:col-3 flex align-items-center">
            <div style={{ backgroundColor: availLevelColors.none, width: '20px', height: '20px', marginRight: '8px' }} />
            <span>None - No availability</span>
          </div>
          <div className="col-6 md:col-3 flex align-items-center">
            <div style={{ backgroundColor: availLevelColors.low, width: '20px', height: '20px', marginRight: '8px' }} />
            <span>Low - 1-3 suites available</span>
          </div>
          <div className="col-6 md:col-3 flex align-items-center">
            <div style={{ backgroundColor: availLevelColors.medium, width: '20px', height: '20px', marginRight: '8px' }} />
            <span>Medium - 4-7 suites available</span>
          </div>
          <div className="col-6 md:col-3 flex align-items-center">
            <div style={{ backgroundColor: availLevelColors.high, width: '20px', height: '20px', marginRight: '8px' }} />
            <span>High - 8+ suites available</span>
          </div>
        </div>
      </div>
    </div>
  )
}
